import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row } from 'antd';
import { useDictionaryTranslation } from '../../hooks/useDictionaryTranslation';
import { Button } from '../buttons/buttons';

const SignaturePad = ({ onSave, disabled, signatureURL }) => {
  const { translate: t } = useDictionaryTranslation();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const points = useRef([]);

  useEffect(() => {
    if (disabled || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (container) {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    }

    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    contextRef.current = ctx;
  }, [disabled]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX;
    let clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX ?? e.nativeEvent.offsetX;
      clientY = e.clientY ?? e.nativeEvent.offsetY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    points.current = [{ x, y }];
    setIsDrawing(true);
  };

  const drawCurve = () => {
    const ctx = contextRef.current;
    if (points.current.length < 2) return;

    ctx.beginPath();
    ctx.moveTo(points.current[0].x, points.current[0].y);

    for (let i = 1; i < points.current.length - 1; i += 1) {
      const midPointX = (points.current[i].x + points.current[i + 1].x) / 2;
      const midPointY = (points.current[i].y + points.current[i + 1].y) / 2;
      ctx.quadraticCurveTo(points.current[i].x, points.current[i].y, midPointX, midPointY);
    }

    ctx.stroke();
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const { x, y } = getCoordinates(e);
    points.current.push({ x, y });
    drawCurve();
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    let top = canvas.height;
    let left = canvas.width;
    let bottom = 0;
    let right = 0;

    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        const index = (y * canvas.width + x) * 4;
        if (pixels[index + 3] > 0) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }

    if (right - left > 0 && bottom - top > 0) {
      const newWidth = right - left;
      const newHeight = bottom - top;
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = newWidth;
      croppedCanvas.height = newHeight;
      const croppedCtx = croppedCanvas.getContext('2d');
      croppedCtx.putImageData(ctx.getImageData(left, top, newWidth, newHeight), 0, 0);
      const dataURL = croppedCanvas.toDataURL('image/png');
      onSave(dataURL);
    } else {
      onSave('');
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    contextRef.current.clearRect(0, 0, canvas.width, canvas.height);
    points.current = [];
  };

  return (
    <>
      {!disabled ? (
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid #e4e6ef',
            borderRadius: '5px',
            touchAction: 'none',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: '100%' }}
            // Mouse events
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            // Touch events
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <Row justify="end">
            <Button onClick={clearCanvas} type="gray" style={{ marginTop: '10px' }}>
              {t('clear')}
            </Button>
          </Row>
        </div>
      ) : signatureURL ? (
        <img style={{ maxWidth: '100%' }} src={signatureURL} alt="sign" />
      ) : null}
    </>
  );
};

SignaturePad.propTypes = {
  onSave: PropTypes.func,
  disabled: PropTypes.bool,
  signatureURL: PropTypes.string,
};

export default SignaturePad;
